import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import Layout from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { Eyebrow } from "../components/bits";
import ChargeRing from "../components/ChargeRing";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import ConsentLine from "../components/ConsentLine";

const INTERESTS = ["Home", "Business", "Venue", "OEM partner", "Press"];

export default function Join() {
  const [form, setForm] = useState({ name: "", email: "", interest: "Home", devices: "", zip_code: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/waitlist", {
        name: form.name,
        email: form.email,
        interest: form.interest,
        devices: form.devices ? Number(form.devices) : null,
        zip_code: form.zip_code || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout showInvestor>
      <section className="relative flex min-h-screen items-center overflow-hidden bg-navy px-5 py-32 md:px-8">
        <div className="absolute -right-40 top-20 hidden lg:block"><ChargeRing size={520} opacity={0.4} /></div>
        <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Waitlist / contact</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tight text-white sm:text-6xl">
              Be one of the first <span className="text-revo-green">1,000.</span>
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-revo-ice">
              First 1,000 homes: professional installation free — plus first hardware allocation. Tell us about your space and devices — it takes 30 seconds.
            </p>
            <ul className="mt-8 space-y-3 text-revo-ice">
              {["Free professional installation", "First hardware allocation", "Founding-member perks"].map((b) => (
                <li key={b} className="flex items-center gap-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-revo-green text-navy"><Check className="h-4 w-4" /></span>{b}</li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl border border-revo-green/40 bg-revo-green/5 p-10 text-center" data-testid="join-confirmation">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-revo-green charge-pulse"><Check className="h-8 w-8 text-navy" /></span>
                <h2 className="mt-6 font-display text-3xl font-black text-white">You're on the list.</h2>
                <p className="mt-3 text-revo-ice">We've sent a confirmation to <span className="font-semibold text-white">{form.email}</span>. Welcome to the future of power — you'll never plug in again.</p>
                <button onClick={() => { setDone(false); setForm({ name: "", email: "", interest: "Home", devices: "", zip_code: "" }); }} className="mt-6 text-sm font-semibold text-revo-green hover:underline" data-testid="join-another-btn">Register another →</button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-white/[0.03] p-8" data-testid="join-form">
                <div className="space-y-4">
                  <Field label="Name" value={form.name} onChange={set("name")} required testid="join-name" />
                  <Field label="Email" type="email" value={form.email} onChange={set("email")} required testid="join-email" />
                  <div>
                    <label className="mb-2 block text-sm font-medium text-revo-ice">I'm interested as</label>
                    <div className="flex flex-wrap gap-2" data-testid="join-interest">
                      {INTERESTS.map((i) => (
                        <button type="button" key={i} onClick={() => setForm((f) => ({ ...f, interest: i }))} data-testid={`interest-${i.replace(/\s+/g, "-")}`} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${form.interest === i ? "bg-revo-green text-navy" : "border border-white/15 text-revo-ice hover:border-revo-green"}`}>{i}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Number of devices" type="number" value={form.devices} onChange={set("devices")} testid="join-devices" />
                    <Field label="ZIP code" value={form.zip_code} onChange={set("zip_code")} testid="join-zip" />
                  </div>
                </div>
                <button type="submit" disabled={busy} data-testid="join-submit-btn" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-revo-green py-4 font-bold text-navy transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60">
                  {busy ? "Reserving…" : <>Reserve my spot <ArrowRight className="h-4 w-4" /></>}
                </button>
                <div className="mt-4"><ConsentLine dark testid="join-consent" /></div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-revo-ice">{label}{required && <span className="text-revo-green"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid} className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-revo-ice/40 focus:border-revo-green focus:outline-none" />
    </div>
  );
}
